import { useMemo } from 'react';

import type { User } from '@/types/directory/User';
import type { ObjectiveOrDraft } from '@/types/objectives';

import { useGetUsersByIds } from '@/api/directory';
import { ObjectiveStatus } from '@/constants/ObjectiveStatus';
import { objectiveHolderIds } from '@/utils/objectiveHolders';
import { getFullName } from '@/utils/user';

export interface OwnerGroupData {
    /** Null for the drafts saved before their employee was chosen. */
    ownerId: string | null;
    owner: User | undefined;
    objectives: ObjectiveOrDraft[];
}

/** Case- and accent-insensitive normalization for the employee-name search. */
const normalize = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

const isDraft = (objective: ObjectiveOrDraft): boolean =>
    objective.status === ObjectiveStatus.DRAFT;

/**
 * The people an objective is listed under: each holder the reader sees, so a team objective shows
 * in every one of its participants' groups. A draft is filed the same way as a published objective,
 * under everyone contributing to it, a team's members included (#16623). Only a draft that names
 * nobody yet keeps its own group, under the owner it does not have.
 */
const listedUnderIds = (objective: ObjectiveOrDraft): (string | null)[] =>
    isDraft(objective) && objective.visibleHolderEmployeeIds.length === 0
        ? [objective.ownerEmployeeId]
        : objective.visibleHolderEmployeeIds;

/** One objective in one person's group. */
interface ListEntry {
    ownerId: string | null;
    objective: ObjectiveOrDraft;
}

/**
 * The people a search or a filter matches an entry by: only the person whose group it sits in, so
 * looking someone up shows that person and nobody else -- never the group of the objective's owner
 * or of another participant.
 */
const matchedPeopleIds = ({ ownerId }: ListEntry): string[] => (ownerId ? [ownerId] : []);

const byDueDateThenTitle = (a: ObjectiveOrDraft, b: ObjectiveOrDraft): number => {
    if (a.endDate === b.endDate) {
        return a.title.localeCompare(b.title);
    }

    // A draft saved without dates has no due date to sort by: it follows the dated objectives.
    if (!a.endDate || !b.endDate) {
        return a.endDate ? -1 : 1;
    }

    return a.endDate.localeCompare(b.endDate);
};

/**
 * Resolves every owner and participant referenced by `objectives` in one grouped call
 * (`getUsersByIds`), then groups the objectives by person and filters by employee name.
 * The backend only filters by pillar and date window: grouping, sorting and search
 * all happen here, client-side.
 *
 * A published objective appears in the group of each holder the reader sees, and a group holds
 * each objective once. The name search and `filterEmployeeIds` -- the set of employee ids the
 * company-attribute filter resolved to -- both keep the groups of the people they match. Without
 * the set there is no filter; an empty set is a filter that names no one, and keeps no group.
 *
 * A draft sits under each of its contributors, as a published objective does, and is also found by
 * its title, since it may not name anyone yet; the attribute filter, which is about employees, hides
 * a draft none of whose contributors is among them.
 */
export const useObjectiveOwners = (
    objectives: ObjectiveOrDraft[],
    searchText: string,
    filterEmployeeIds?: Set<string>,
) => {
    const employeeIds = useMemo(() => {
        const ids = new Set<string>();
        objectives.forEach((objective) =>
            objectiveHolderIds(objective).forEach((id) => ids.add(id)),
        );
        return Array.from(ids);
    }, [objectives]);

    const {
        data: employees = [],
        isLoading,
        error,
    } = useGetUsersByIds({ userIds: employeeIds, identifier: 'objective-owners' });

    const employeesById = useMemo(() => {
        const map = new Map<string, User>();
        employees.forEach((employee) => map.set(employee.id, employee));
        return map;
    }, [employees]);

    const normalizedSearch = normalize(searchText.trim());

    const groups = useMemo<OwnerGroupData[]>(() => {
        const matchesSearch = (entry: ListEntry): boolean => {
            if (!normalizedSearch) {
                return true;
            }

            if (
                isDraft(entry.objective) &&
                normalize(entry.objective.title).includes(normalizedSearch)
            ) {
                return true;
            }

            return matchedPeopleIds(entry).some((id) => {
                const user = employeesById.get(id);
                return !!user && normalize(getFullName(user)).includes(normalizedSearch);
            });
        };

        const matchesFilter = (entry: ListEntry): boolean => {
            if (!filterEmployeeIds) {
                return true;
            }

            return matchedPeopleIds(entry).some((id) => filterEmployeeIds.has(id));
        };

        const byOwner = new Map<string | null, ObjectiveOrDraft[]>();
        objectives
            .flatMap((objective) =>
                // A holder named twice by the server still gets the objective once in their group.
                [...new Set(listedUnderIds(objective))].map((ownerId) => ({ ownerId, objective })),
            )
            .filter((entry) => matchesSearch(entry) && matchesFilter(entry))
            .forEach(({ ownerId, objective }) => {
                const list = byOwner.get(ownerId) ?? [];
                list.push(objective);
                byOwner.set(ownerId, list);
            });

        const result: OwnerGroupData[] = Array.from(byOwner.entries()).map(
            ([ownerId, ownerObjectives]) => ({
                ownerId,
                owner: ownerId ? employeesById.get(ownerId) : undefined,
                objectives: [...ownerObjectives].sort(byDueDateThenTitle),
            }),
        );

        // A group whose owner the directory could not resolve sorts after the named ones.
        return result.sort((a, b) => {
            if (!a.owner && !b.owner) {
                return 0;
            }

            if (!a.owner) {
                return 1;
            }

            if (!b.owner) {
                return -1;
            }

            return getFullName(a.owner).localeCompare(getFullName(b.owner), undefined, {
                sensitivity: 'base',
            });
        });
    }, [objectives, employeesById, normalizedSearch, filterEmployeeIds]);

    return { groups, employeesById, isLoading, error };
};
